/**
 * 出庫 · 發 WhatsApp 通知面板（**V4 範本**；`decisions/009`）
 *
 * 做乜：一按 → `POST /api/outbound-orders/:id/send`（後端讀 `settings` 嘅範本名／語言／收訊人，
 *   砌 V4 payload，逐個收訊人發）→ 成功 ＝ `draft → sent`（之後才准執貨）。
 *
 * 紅線
 *   · ⚠️ 收訊人電話**只會以遮蔽形式**顯示（後端已遮蔽；前端唔准自己拼返原號）
 *   · ⚠️ 「預覽（唔發）」＝ `dryRun`：只回 payload ＋ deep link，**唔會發任何訊息**
 *   · ⚠️ 發唔到**唔算成功**：後端會回 502 同失敗原因；狀態留喺 `draft`（可以改完再發）
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { btnGhost, btnPrimary, hint as hintStyle, errText } from '@coeldery/ui-kit';
import { ApiError, api, type SendResult } from '../lib/api';

export interface OutboundSendPanelProps {
  orderId: string;
  /** 發完之後叫上層重載詳情（狀態由後端決定） */
  onDone: () => Promise<void>;
}

export default function OutboundSendPanel({ orderId, onDone }: OutboundSendPanelProps) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<'send' | 'preview' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<SendResult | null>(null);
  const [sent, setSent] = useState<SendResult | null>(null);

  const run = async (dryRun: boolean) => {
    setBusy(dryRun ? 'preview' : 'send');
    setError(null);
    try {
      const res = await api.sendOutbound(orderId, dryRun);
      if (dryRun) setPreview(res);
      else {
        setSent(res);
        await onDone();
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('out.sendFailed'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={{ border: '1px dashed var(--color-divider)', borderRadius: '12px', padding: '12px', marginTop: '10px' }}>
      <strong style={{ fontSize: '14px' }}>{t('out.sendTitle')}</strong>
      <p style={hintStyle}>{t('out.sendHint')}</p>

      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '8px' }}>
        <button type="button" style={btnPrimary} disabled={busy !== null} onClick={() => void run(false)}>
          {busy === 'send' ? t('out.sending') : t('out.send')}
        </button>
        <button type="button" style={btnGhost} disabled={busy !== null} onClick={() => void run(true)}>
          {busy === 'preview' ? t('common.loading') : t('out.preview')}
        </button>
      </div>

      {error && (
        <p style={errText} role="alert">
          {error}
        </p>
      )}

      {preview && (
        <div style={{ marginTop: '8px' }}>
          <p style={hintStyle}>{t('out.previewResult')}</p>
          <p style={hintStyle}>
            {t('out.template')}：{preview.templateName} · {t('out.lang')}：{preview.lang}
          </p>
          <p style={hintStyle}>
            {t('out.deepLink')}：{preview.deepLink ?? '—'}
          </p>
          <p style={hintStyle}>
            {t('out.recipients')}：{(preview.recipients ?? []).join('、') || '—'}
          </p>
        </div>
      )}

      {sent && (
        <div style={{ marginTop: '8px' }}>
          <p style={{ ...hintStyle, color: 'var(--color-primary)', fontWeight: 700 }}>{t('out.sendOk')}</p>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {(sent.results ?? []).map((r) => (
              <li key={r.to} style={hintStyle}>
                {r.ok ? '✅' : '❌'} {r.to}
                {r.ok ? '' : ` — ${r.hint ?? ''}`}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
